import React, {FC, useCallback, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import {showErrorToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {formatDateTime} from '../shared/DocumentApi'
import {searchInterviews} from './Api'
import {
  IInterviewFilters,
  IInterviewModel,
  INTERVIEW_STATUS_BADGE,
  INTERVIEW_STATUS_LABEL,
  INTERVIEW_STATUS_OPTIONS,
  interviewTimeRange,
} from './InterviewModel'
import {InterviewNotesModal} from './InterviewNotesModal'
import {InterviewStatusAction, InterviewStatusModal} from './InterviewStatusModal'

const MIN_SEARCH_LENGTH = 3
const emptyFilters: IInterviewFilters = {q: '', status: '', from: '', to: ''}

/** Mülakat listesi: not ekleme, gerçekleşti / iptal işaretleme */
const InterviewPage: FC = () => {
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState<Omit<IInterviewFilters, 'q'>>({status: '', from: '', to: ''})
  const [filters, setFilters] = useState<IInterviewFilters>(emptyFilters)
  const [interviews, setInterviews] = useState<IInterviewModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [notesFor, setNotesFor] = useState<IInterviewModel | null>(null)
  const [statusFor, setStatusFor] = useState<{interview: IInterviewModel; action: InterviewStatusAction} | null>(null)

  const fetchInterviews = useCallback(async (page: number, current: IInterviewFilters) => {
    setLoading(true)
    try {
      const payload = (await searchInterviews(current, page)).data.payload
      setInterviews(payload.content)
      setTotalPages(payload.totalPages)
      setCurrentPage(page)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Mülakatlar yüklenemedi'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchInterviews(0, filters)
  }, [filters, fetchInterviews])

  // Arama 3 karakterden itibaren otomatik çalışır
  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH) return
    const timer = setTimeout(() => setFilters((current) => (current.q === trimmed ? current : {...current, q: trimmed})), 400)
    return () => clearTimeout(timer)
  }, [query])

  const reload = () => fetchInterviews(currentPage, filters)

  const columns: TableColumn<IInterviewModel>[] = [
    {
      key: 'candidateName',
      label: 'Aday',
      render: (_, interview) => (
        <div className='d-flex flex-column'>
          {interview.candidateId ? (
            <Link to={`/hr/candidate/detail/${interview.candidateId}`} className='fw-bolder text-gray-800 text-hover-primary'>
              {interview.candidateName}
            </Link>
          ) : (
            <span className='fw-bolder text-gray-800'>{interview.candidateName}</span>
          )}
          <span className='text-muted fs-7'>{interview.candidateEmail}</span>
        </div>
      ),
    },
    {
      key: 'title',
      label: 'Mülakat',
      render: (_, interview) => (
        <div className='d-flex flex-column'>
          <span className='fw-semibold'>{interview.title}</span>
          {interview.jobTitle && <span className='text-muted fs-7'>{interview.jobTitle}</span>}
          {interview.organizerUserName && (
            <span className='text-muted fs-8'>
              <i className='bi bi-person me-1'></i>
              {interview.organizerUserName}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'start',
      label: 'Tarih',
      render: (_, interview) => (
        <div className='d-flex flex-column'>
          <span className='fw-semibold'>{interviewTimeRange(interview)}</span>
          {interview.meetLink && (
            <a href={interview.meetLink} target='_blank' rel='noopener noreferrer' className='fs-7'>
              <i className='bi bi-camera-video me-1'></i>
              Toplantı linki
            </a>
          )}
        </div>
      ),
    },
    {
      key: 'participants',
      label: 'Katılımcılar',
      render: (_, interview) =>
        interview.participants.length === 0 ? (
          <span className='text-muted'>-</span>
        ) : (
          <div className='d-flex flex-column fs-7'>
            {interview.participants.map((participant) => (
              <span key={participant.email} title={participant.email}>
                {participant.name || participant.email}
              </span>
            ))}
          </div>
        ),
    },
    {
      key: 'status',
      label: 'Durum',
      width: '170px',
      render: (_, interview) => (
        <div className='d-flex flex-column gap-1'>
          <span className={`badge ${INTERVIEW_STATUS_BADGE[interview.status]} align-self-start`}>
            {INTERVIEW_STATUS_LABEL[interview.status]}
          </span>
          {interview.status === 'COMPLETED' && (
            <span className='text-muted fs-8'>{formatDateTime(interview.completedAt)}</span>
          )}
          {interview.status === 'CANCELLED' && interview.cancelReason && (
            <span className='text-danger fs-8'>{interview.cancelReason}</span>
          )}
        </div>
      ),
    },
  ]

  const actions: TableAction<IInterviewModel>[] = [
    {
      label: 'Not Ekle',
      icon: 'bi bi-journal-text',
      className: 'btn-light-primary me-2',
      onClick: setNotesFor,
    },
    {
      label: 'Gerçekleşti',
      icon: 'bi bi-check2-circle',
      className: 'btn-light-success me-2',
      onClick: (interview) => setStatusFor({interview, action: 'complete'}),
      condition: (interview) => interview.status === 'PLANNED',
    },
    {
      label: 'İptal',
      icon: 'bi bi-x-circle',
      className: 'btn-light-danger',
      onClick: (interview) => setStatusFor({interview, action: 'cancel'}),
      condition: (interview) => interview.status === 'PLANNED',
    },
  ]

  return (
    <>
      <PageTitle breadcrumbs={[]}>Mülakatlar</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Mülakatlar</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Mülakatlar Aday Takip ekranındaki "Mülakat" butonuyla planlanır
            </span>
          </h3>
        </div>
        <div className='card-body pt-0'>
          <div className='d-flex flex-wrap align-items-center gap-3 mb-6'>
            <div className='position-relative flex-grow-1' style={{minWidth: 240, maxWidth: 360}}>
              <i className='bi bi-search position-absolute top-50 translate-middle-y ms-4 text-gray-500'></i>
              <input
                type='search'
                className='form-control form-control-solid ps-12'
                placeholder='Aday, e-posta, ilan veya başlık ile ara'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <select
              className='form-select form-select-solid w-auto'
              value={draft.status}
              onChange={(e) => setDraft({...draft, status: e.target.value as IInterviewFilters['status']})}
            >
              <option value=''>Tüm durumlar</option>
              {INTERVIEW_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type='date'
              className='form-control form-control-solid w-auto'
              title='Başlangıç tarihi'
              value={draft.from}
              onChange={(e) => setDraft({...draft, from: e.target.value})}
            />
            <input
              type='date'
              className='form-control form-control-solid w-auto'
              title='Bitiş tarihi'
              value={draft.to}
              onChange={(e) => setDraft({...draft, to: e.target.value})}
            />
            <button type='button' className='btn btn-sm btn-primary' onClick={() => setFilters({...filters, ...draft})}>
              <i className='bi bi-funnel'></i>
              Filtrele
            </button>
            {(filters.status || filters.from || filters.to) && (
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() => {
                  setDraft({status: '', from: '', to: ''})
                  setFilters({...filters, status: '', from: '', to: ''})
                }}
              >
                Temizle
              </button>
            )}
          </div>

          <DataTable
            data={interviews}
            columns={columns}
            actions={actions}
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => page >= 0 && page < totalPages && fetchInterviews(page, filters)}
            loading={loading}
            emptyMessage='Mülakat bulunamadı'
          />
        </div>
      </div>

      <InterviewNotesModal interview={notesFor} onClose={() => setNotesFor(null)} onNoteAdded={reload} />

      <InterviewStatusModal
        interview={statusFor?.interview ?? null}
        action={statusFor?.action ?? 'complete'}
        onClose={() => setStatusFor(null)}
        onSaved={reload}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {InterviewPage}
